import { AxiosError, type AxiosRequestConfig } from "axios";
import { callCashBoxApi, convertToCustomErrorType } from "../helpers/apiUtilities";

interface SendAxiosRequestWithRetryOptions {
    maxRetries?: number;
    retryDelay?: number;
    retryNum?: number;
}

/**
 * Simple axios request with retry (CashBox friendly)
 */
export const sendAxiosRequestWithRetry = async <DataType = unknown>(
    config: AxiosRequestConfig,
    options: SendAxiosRequestWithRetryOptions = {}
): Promise<ApiResult<DataType>> => {
    const {
        maxRetries = process.env.NODE_ENV === "development" ? 3 : 1,
        retryDelay = 1000,
        retryNum = 1,
    } = options;

    try {
        const response = await callCashBoxApi<DataType>(config);

        if (response instanceof AxiosError) {
            throw response;
        }

        return { data: response.data?.data };
    }
    catch (err) {
        const typedErr = err as AxiosError;
        const status = typedErr?.response?.status;

        const shouldRetry =
            status === 500 &&
            retryNum < maxRetries;

        if (shouldRetry) {
            return new Promise<ApiResult<DataType>>((resolve) => {
                setTimeout(async () => {
                    const result = await sendAxiosRequestWithRetry<DataType>(
                        config,
                        {
                            maxRetries,
                            retryDelay,
                            retryNum: retryNum + 1,
                        }
                    );
                    resolve(result);
                }, retryDelay);
            });
        }

        return { error: convertToCustomErrorType(err) };
    }
};
